import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";

export interface PaymentCategory {
  id: string;
  gym_id: string;
  name: string;
  amount: number;
  validity_days: number;
  covers_people: number;
  is_active: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface CreateCategoryData {
  gym_id: string;
  name: string;
  amount: number;
  validity_days: number;
  covers_people?: number;
}

export interface UpdateCategoryData {
  name?: string;
  amount?: number;
  validity_days?: number;
  covers_people?: number;
  is_active?: boolean;
  position?: number;
}

export const usePaymentCategories = (gymId: string | null) => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotifications();

  const { data: categories, isLoading, error } = useQuery({
    queryKey: ["payment-categories", gymId],
    queryFn: async () => {
      if (!gymId) return [];
      
      const { data, error } = await supabase
        .from("payment_categories")
        .select("*")
        .eq("gym_id", gymId)
        .eq("is_active", true)
        .order("position", { ascending: true });

      if (error) throw error;
      return data as PaymentCategory[];
    },
    enabled: !!gymId,
  });

  const createCategory = useMutation({
    mutationFn: async (data: CreateCategoryData) => {
      const { data: result, error } = await supabase
        .from("payment_categories")
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-categories", gymId] });
      success("Categoría creada", { description: "La categoría se creó correctamente" });
    },
    onError: (error) => {
      console.error("Error creating category:", error);
      showError("No se pudo crear la categoría");
    },
  });

  const updateCategory = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCategoryData }) => {
      const { data: result, error } = await supabase
        .from("payment_categories")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-categories", gymId] });
      success("Categoría actualizada", { description: "La categoría se actualizó correctamente" });
    },
    onError: (error) => {
      console.error("Error updating category:", error);
      showError("No se pudo actualizar la categoría");
    },
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("payment_categories")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-categories", gymId] });
      success("Categoría eliminada", { description: "La categoría se eliminó correctamente" });
    },
    onError: (error) => {
      console.error("Error deleting category:", error);
      showError("No se pudo eliminar la categoría");
    },
  });

  return {
    categories: categories || [],
    isLoading,
    error,
    createCategory,
    updateCategory,
    deleteCategory,
  };
};
